/**
 * Dekorative Bewegtbilder laufen erst, wenn sie im sichtbaren Bereich stehen.
 *
 * Zwei Arten, ein Beobachter:
 *
 *   <video> in <deferred-media data-mk-decorative-video>  (snippets/video.liquid)
 *     Horizons video_tag rendert immer `autoplay` — das Video liefe sonst schon,
 *     bevor jemand es sehen kann. Wird hier gestartet und beim Verlassen pausiert.
 *
 *   <img data-mk-animation="…">  (blocks/image.liquid)
 *     Sichtbar ist zunaechst das Poster, also das erste Einzelbild. Beim Eintritt
 *     in den Sichtbereich wird die animierte Datei eingesetzt; ihre Schleifenzahl
 *     steht auf 1, sie laeuft also genau einmal und bleibt am Schlussbild stehen.
 *     Anhalten geht bei Bildern nicht — einmal gestartet, laeuft sie durch.
 *
 * Wer weniger Bewegung eingestellt hat, bekommt gar keine Animation — es bleibt
 * beim ersten Bild.
 */
const SELECTOR = '[data-mk-decorative-video], [data-mk-animation]';
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @type {IntersectionObserver | undefined} */
let observer;
/** @type {WeakSet<Element>} */
const known = new WeakSet();

/**
 * Sicherheitsnetz fuers Video: `autoplay` und `preload` unterbindet bereits das
 * Liquid (snippets/video.liquid, decorative-Zweig). Kommt ein Video doch mit
 * autoplay herein — etwa aus einer aelteren gecachten Seite — wird es angehalten.
 * @param {HTMLVideoElement} video
 */
function halt(video) {
  video.removeAttribute('autoplay');
  video.pause();
}

/**
 * Kein Vorladen von Hand: waehrend die neue Datei laedt, zeigt das <img> weiter
 * das Poster (HTML-Spezifikation, "pending request") — es blitzt also nichts.
 * Ein fetch() zum Vorwaermen waere sogar schaedlich: Shopifys CDN liefert die
 * Datei je nach Accept-Header als WebP oder als GIF aus (gemessen 293 KB gegen
 * 514 KB), ein fetch mit anderem Accept laedt also die falsche Variante
 * zusaetzlich herunter.
 * @param {HTMLImageElement} img
 */
function startAnimation(img) {
  const src = img.dataset.mkAnimation;
  if (!src || img.dataset.mkAnimationStarted) return;
  img.dataset.mkAnimationStarted = 'true';
  img.src = src;
}

function handle(/** @type {IntersectionObserverEntry[]} */ entries) {
  for (const entry of entries) {
    if (entry.target instanceof HTMLImageElement) {
      if (entry.isIntersecting) {
        if (!reduceMotion.matches) startAnimation(entry.target);
        observer?.unobserve(entry.target);
      }
      continue;
    }

    const video = entry.target.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) continue;

    // Durchgelaufen heisst fertig: play() wuerde sonst von vorn beginnen, sobald
    // jemand ein zweites Mal daran vorbeiscrollt. Das Video bleibt am Schlussbild.
    if (video.ended) {
      observer?.unobserve(entry.target);
      continue;
    }

    if (entry.isIntersecting && !reduceMotion.matches) {
      // play() kann abgelehnt werden (Autoplay-Policy) — das ist hier kein Fehlerfall.
      video.play().catch(() => {});
    } else {
      // Ausserhalb anhalten und beim Zurueckscrollen fortsetzen, nicht neu starten:
      // so laeuft die Animation ueber den Seitenbesuch hinweg genau einmal durch.
      video.pause();
    }
  }
}

function scan() {
  const targets = document.querySelectorAll(SELECTOR);
  if (!targets.length) return;

  observer ||= new IntersectionObserver(handle, { threshold: 0.2 });

  for (const target of targets) {
    if (known.has(target)) continue;
    known.add(target);
    const video = target.querySelector?.('video');
    if (video instanceof HTMLVideoElement) halt(video);
    observer.observe(target);
  }
}

scan();

// Section-Rendering-API und Theme-Editor tauschen Markup aus; neue Medien nachziehen.
document.addEventListener('shopify:section:load', scan);
document.addEventListener('shopify:block:select', scan);
new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
